import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCommunicators } from '../../hooks/useFirestore';
import { cn } from '../../utils/cn';
import { motion, AnimatePresence } from 'framer-motion';

const NavItem = ({ to, children }) => {
  const loc = useLocation();
  const active = loc.pathname === to || loc.pathname.startsWith(to + '/');
  return (
    <NavLink
      to={to}
      className={cn(
        'relative px-4 py-2 rounded-xl text-sm font-semibold transition-colors',
        active ? 'text-accent-700' : 'text-navy-500 hover:text-navy-700 hover:bg-surface-100',
      )}
    >
      {active && (
        <motion.span
          layoutId="nav-pill"
          className="absolute inset-0 bg-accent-50 border border-accent-200 rounded-xl"
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        />
      )}
      <span className="relative z-10">{children}</span>
    </NavLink>
  );
};

/* ── Communicator Picker Dropdown (admin only) ── */
function CommunicatorPicker() {
  const { startImpersonating, isAdmin, isPeoplePartner, ppAssignedEmails } = useAuth();
  const { communicators: allCommunicators, loading: allLoading } = useCommunicators(isAdmin);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // For PPs, derive communicator list from assigned emails
  const communicators = useMemo(() => {
    if (isAdmin) return allCommunicators;
    return (ppAssignedEmails || []).map(email => ({
      email,
      name: email.split('@')[0].split(/[._]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    }));
  }, [isAdmin, allCommunicators, ppAssignedEmails]);

  const loading = isAdmin ? allLoading : false;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = communicators.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s);
  });

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition-colors"
        title="View as Communicator"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        View as…
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-surface-200 z-50 overflow-hidden"
          >
            <div className="p-3 border-b border-surface-100">
              <input
                autoFocus
                placeholder="Search communicators…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded-xl border border-surface-300 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-400 transition-all"
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {loading ? (
                <p className="text-xs text-navy-400 px-4 py-3">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="text-xs text-navy-400 px-4 py-3">No communicators found</p>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.email}
                    onClick={() => {
                      startImpersonating(c.email, c.name);
                      setOpen(false);
                      setSearch('');
                      nav('/communicator');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-400 to-cyan-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {(c.name || c.email)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-navy-800 truncate">{c.name}</p>
                      <p className="text-xs text-navy-400 truncate">{c.email}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { user, isAdmin, isCommunicator, isPeoplePartner, signOut, impersonating, stopImpersonating } = useAuth();
  const nav = useNavigate();
  if (!user) return null;

  const showAdminNav = isAdmin && !impersonating;
  const showPicker = (isAdmin || isPeoplePartner) && !impersonating;

  return (
    <>
      {/* Impersonation banner */}
      <AnimatePresence>
        {impersonating && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-indigo-600 text-white overflow-hidden z-[60] relative"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-2">
              <div className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>Viewing as <strong>{impersonating.name}</strong></span>
                <span className="text-indigo-200">({impersonating.email})</span>
              </div>
              <button
                onClick={() => { stopImpersonating(); nav('/admin'); }}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-white/20 hover:bg-white/30 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Stop viewing
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="sticky top-0 z-50 glass border-b border-surface-200/60">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-6">
          {/* Left: Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-accent-500 to-cyan-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-base font-bold text-navy-900 tracking-tight hidden sm:block">
              Comp Companion
            </span>
          </div>

          {/* Center: Nav */}
          <nav className="flex items-center gap-1">
            {showAdminNav && <NavItem to="/admin">Dashboard</NavItem>}
            {showAdminNav && <NavItem to="/admin/users">Users</NavItem>}
            {(isCommunicator || impersonating) && <NavItem to="/communicator">My People</NavItem>}
          </nav>

          {/* Right: User + impersonate */}
          <div className="flex items-center gap-3">
            {showPicker && <CommunicatorPicker />}
            <span className="text-xs text-navy-400 hidden md:block">{user.email}</span>
            <button
              onClick={async () => { await signOut(); nav('/', { replace: true }); }}
              className="p-2 rounded-xl hover:bg-surface-100 transition-colors text-navy-400 hover:text-navy-600"
              title="Sign out"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </button>
          </div>
        </div>
      </header>
    </>
  );
}
